import { Outlet } from 'react-router-dom';

import { Sidebar } from '../components/Sidebar';
import { Topbar } from '../components/Topbar';
import { ToastContainer } from '../components/ui/ToastContainer';
import { VariantBanner } from '../components/VariantBanner';
import { useCoachingToast } from '../features/conversations/supervision/use-coaching-toast';
import { useDirectMessagesWsSync } from '../features/direct-messages/hooks/use-direct-messages-ws-sync';
import { Spotlight } from '../features/spotlight/Spotlight';
import { useSpotlightOpen } from '../features/spotlight/use-spotlight-open';
import { useAuth } from '../hooks/use-auth';
import { useIntegrationsFeatureToggleInvalidate } from '../hooks/use-integrations-feature-toggle-invalidate';

/**
 * AppShell canonical — Sprint Visual Brief 2A r7b (Agent A SHELL).
 *
 * Layout canônico do app autenticado replicando o protótipo Claude
 * Design (`design-references/brief-2a/r7b/app.jsx` linha 102-273):
 *
 *   .app-shell           (grid rows: 52px topbar + auto variant-banner + 1fr body)
 *     <Topbar />         brand + tenant pill + spacer + search + bell + cog + user
 *     <VariantBanner />  amarelo: variant + tenant + user + capabilities count
 *     .app-body          (grid cols: 280px sidebar + 1fr main)
 *       <Sidebar />      nav primária + "minhas conversas" preview + footer
 *       <Outlet />       conteúdo da rota (Atendimento, Inbox, Supervisor, etc.)
 *
 * O painel direito (right-panel 360px) é responsabilidade da Conversa em
 * curso e renderiza dentro da rota `atendimento/conversation/:id` — não
 * é layout global. Outras rotas (inbox, supervisor, admin/*) usam o
 * espaço inteiro à direita da sidebar.
 *
 * Anti-pattern PR #75 evitado: classes CSS canonical (.app-shell,
 * .topbar, .sidebar, .variant-banner) têm definições ADITIVAS em
 * `styles/global.css` (Sprint Visual Agent A SHELL section).
 */
export function AppShell(): JSX.Element {
  const { user, tenant, capabilities, logout } = useAuth();

  // Sub-fatia 10.0 PR 3 + Sprint A4 S1 cross-feature — invalida customer-context
  // quando admin pausa/reativa a feature de integrações em background. Listener
  // global no shell garante que ContextBlock em qualquer conversa aberta
  // reflita a regra do silêncio sem refresh manual.
  useIntegrationsFeatureToggleInvalidate();

  // Mensageria interna — tempo real do badge de não-lido + caixa + thread aberto.
  // Montado no shell para o badge atualizar mesmo fora de /mensagens (self-gated
  // na cap direct-message-participate).
  useDirectMessagesWsSync();

  // Supervisão visível ao operador (A-41, 04/Set/2026) — coaching que chega enquanto o
  // operador está em OUTRA tela vira aviso com «Abrir» para a conversa certa. Na conversa
  // aberta, a faixa acima do Resumo Vivo já acende; o hook não duplica.
  useCoachingToast();

  // Localizador Universal (⌘K). Montado no shell porque a paleta abre de
  // QUALQUER tela — é a saída de emergência do operador, não recurso de uma
  // rota. O listener de teclado vive no hook.
  const { open: searchOpen, setOpen: setSearchOpen } = useSpotlightOpen();

  return (
    <div className="app-shell">
      <Topbar
        tenant={tenant}
        user={user}
        onLogout={() => void logout()}
        onOpenSearch={() => setSearchOpen(true)}
      />

      <VariantBanner tenant={tenant} user={user} capabilities={capabilities} />

      <div className="app-body">
        <Sidebar />

        <main className="min-h-0 overflow-auto" aria-label="Conteúdo principal">
          <Outlet />
        </main>
      </div>

      <Spotlight
        open={searchOpen}
        onClose={() => {
          setSearchOpen(false);
        }}
      />

      <ToastContainer />
    </div>
  );
}
